import React from 'react';
import { Shield, Mail, Phone, MapPin } from 'lucide-react';
import { company } from '../data/mockData';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  const sections = [
    {
      title: '1. Information We Collect',
      intro: 'We may collect and process the following types of information:',
      groups: [
        {
          heading: 'a. Personal Information',
          items: [
            'Name, company name, and designation',
            'Email address and phone number',
            'Business address and project details'
          ]
        },
        {
          heading: 'b. Inquiry & Project Data',
          items: [
            'Information shared through contact forms, calls, emails, or meetings',
            'Technical requirements, site details, and operational data'
          ]
        },
        {
          heading: 'c. Technical & Usage Data',
          items: [
            'IP address, browser type, device information',
            'Pages visited, time spent, and interaction data on our website'
          ]
        }
      ]
    },
    {
      title: '2. How We Use Your Information',
      intro: 'Your information is used for legitimate business purposes, including:',
      items: [
        'Responding to inquiries and providing quotations',
        'Understanding your operational requirements',
        'Designing and delivering tailored solutions',
        'Improving our website, products, and services',
        'Communicating updates, proposals, and service-related information'
      ],
      outro: 'We do not use your data for unsolicited or irrelevant marketing.'
    },
    {
      title: '3. Legal Basis for Processing',
      intro: 'We process your information based on:',
      items: [
        'Your consent (when you submit forms or inquiries)',
        'Contractual necessity (to deliver services or quotations)',
        'Legitimate business interests (improving services and communication)'
      ]
    },
    {
      title: '4. Data Sharing and Disclosure',
      intro: 'We do not sell or rent your personal data. Information may be shared only when necessary:',
      items: [
        'With internal teams for execution and support',
        'With trusted vendors or partners involved in project delivery',
        'With legal or regulatory authorities when required'
      ],
      outro: 'All third parties are expected to maintain confidentiality and data security.'
    },
    {
      title: '5. Data Retention',
      intro: 'We retain your information only as long as necessary:',
      items: [
        'For active inquiries and ongoing projects',
        'For legal, accounting, or compliance requirements',
        'For improving future service delivery'
      ],
      outro: 'Once no longer required, data is securely deleted or anonymized.'
    },
    {
      title: '6. Data Security',
      body: 'We implement appropriate technical and organizational measures to protect your data against unauthorized access, loss, misuse, or alteration. However, no system is completely secure, and we cannot guarantee absolute security.'
    },
    {
      title: '7. Cookies and Tracking Technologies',
      intro: 'Our website may use cookies and similar technologies to:',
      items: [
        'Improve website functionality',
        'Analyze traffic and user behavior',
        'Enhance user experience'
      ],
      outro: 'You can control or disable cookies through your browser settings.'
    },
    {
      title: '8. Third-Party Services',
      body: 'We may use third-party tools (such as analytics or communication platforms) to operate our business. These providers may process limited data on our behalf and are expected to comply with applicable data protection standards.'
    },
    {
      title: '9. Your Rights',
      intro: 'You have the right to:',
      items: [
        'Access the personal data we hold about you',
        'Request correction of inaccurate information',
        'Request deletion of your data (subject to legal obligations)',
        'Withdraw consent for communications at any time'
      ],
      outro: 'To exercise these rights, contact us using the details below.'
    },
    {
      title: "10. Children's Privacy",
      body: 'Our services are intended for business and industrial use. We do not knowingly collect personal data from individuals under the age of 18.'
    },
    {
      title: '11. International Data Transfers',
      body: 'If applicable, your data may be processed or stored in locations outside your region. We ensure appropriate safeguards are in place for such transfers.'
    },
    {
      title: '12. Updates to This Privacy Policy',
      body: 'We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. Updated versions will be posted on this page.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#f9fafb]">
      <SEO
        title="Privacy Policy | Stallion Bulktech"
        description="Read the privacy policy of Stallion Bulktech to understand how we collect, use, and protect your information across our website and services."
        path="/privacy-policy"
      />
      {/* Hero */}
      <section className="relative pt-24 pb-10 md:pt-32 md:pb-14 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 border border-cyan-200 rounded-full mb-6">
            <Shield className="h-4 w-4 text-cyan-600" />
            <span className="text-cyan-600 text-sm font-medium">Legal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm md:text-base text-gray-500">
            Effective Date: 15 November 2018
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-6 sm:p-10 md:p-12 shadow-xl">
            <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-10">
              Stallion Bulktech ("we", "our", or "us") is committed to protecting your privacy and ensuring transparency in how your personal information is collected, used, and safeguarded. This Privacy Policy explains how we handle your data when you interact with our website, services, or team.
            </p>

            <div className="space-y-10">
              {sections.map((section) => (
                <div key={section.title}>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                    {section.title}
                  </h2>
                  {section.intro && (
                    <p className="text-gray-700 leading-relaxed mb-3">{section.intro}</p>
                  )}
                  {section.body && (
                    <p className="text-gray-700 leading-relaxed">{section.body}</p>
                  )}
                  {section.items && (
                    <ul className="space-y-2 text-gray-700 list-disc pl-6">
                      {section.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.groups && (
                    <div className="space-y-5">
                      {section.groups.map((g) => (
                        <div key={g.heading}>
                          <h3 className="font-semibold text-gray-800 mb-2">{g.heading}</h3>
                          <ul className="space-y-2 text-gray-700 list-disc pl-6">
                            {g.items.map((it) => <li key={it}>{it}</li>)}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                  {section.outro && (
                    <p className="text-gray-700 leading-relaxed mt-3">{section.outro}</p>
                  )}
                </div>
              ))}

              {/* Contact */}
              <div className="pt-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                  13. Contact Us
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For any questions, concerns, or data-related requests:
                </p>
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-5 sm:p-6 space-y-3">
                  <div className="font-semibold text-gray-900">{company.name}</div>
                  <div className="flex items-start gap-3 text-gray-700">
                    <MapPin className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                    <span>{company.address}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Phone className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                    <span>{company.phone} / {company.phone2}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Mail className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                    <a href={`mailto:${company.email}`} className="text-cyan-700 hover:text-cyan-800 underline underline-offset-2">
                      {company.email}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
